import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const response = await authAPI.getMe();
    setUser(response.data);
    setInstitution(response.data.institution || null);
    return response.data;
  }, []);

  useEffect(() => {
    // Session lives in an httpOnly cookie; probe the backend to restore it
    refreshUser().catch(() => setUser(null)).finally(() => setLoading(false));
  }, [refreshUser]);

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    const userData = response.data.user;
    setUser(userData);
    try {
      await refreshUser();
    } catch {
      // Continue with basic user data
    }
    return userData;
  };

  // Creates a staff user inside the caller's institution; does NOT switch the current session
  const register = async (userData) => {
    const response = await authAPI.register(userData);
    return response.data.user;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {
      // Cookie may already be gone; clear local state regardless
    }
    document.cookie = 'hmc_csrf=; Max-Age=0; path=/; secure; samesite=lax';
    setUser(null);
    setInstitution(null);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') {
      return user.role === roles;
    }
    return roles.includes(user.role);
  };

  const isMasterAdmin = () => user?.is_master_admin === true;

  const isPlatformAdmin = () => user?.role === 'super_admin' && !user?.institution_id;

  const value = {
    user,
    institution,
    loading,
    login,
    register,
    logout,
    refreshUser,
    hasRole,
    isMasterAdmin,
    isPlatformAdmin,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
